import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Icon,
  CircularProgress,
  MenuItem,
  FormControl,
  IconButton,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import usePaymentData, { OFFERING_OCCASIONS } from "layouts/tables/data/offeringTableData";
import PropTypes from "prop-types";

function Payment() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    members,
    isLoadingMembers,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    loadAllMembers,
  } = usePaymentData();

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    if (!selectedPayment?.reference) {
      setSelectedPayment((prev) => ({
        ...prev,
        reference: `PAY-${Date.now().toString().slice(-6)}`,
      }));
    }
  }, [selectedPayment]);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Load members when modal opens
  useEffect(() => {
    if (editModalOpen) {
      loadAllMembers();
    }
  }, [editModalOpen, loadAllMembers]);

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Tithe Payments...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Offerings"
        actions={
          <>
            <Button variant="contained" color="info" onClick={handleAddPayment}>
              <Icon>add</Icon>&nbsp;New Payment
            </Button>
            <Button variant="contained" color="info" onClick={handlePrint}>
              <Icon>print</Icon>
            </Button>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted={false}
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          noEndBorder
          canSearch
          searchLabel="Search offerings..."
        />
      </Card>

      {/* Edit/Create Payment Dialog */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedPayment?.id ? "Edit Payment" : "Create New Payment"}
        onSave={handleEditSubmit}
        saveLabel={selectedPayment?.id ? "Save Changes" : "Create Payment"}
        maxWidth="md"
      >
        <FormSection>
          <FormControl fullWidth>
            <TextField
              label="Search Member (optional — leave blank for a collective offering)"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowMemberDropdown(e.target.value.trim() !== "");
              }}
              onFocus={() => {
                if (searchTerm.trim() !== "" && filteredMembers.length > 0) {
                  setShowMemberDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowMemberDropdown(false), 200)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <>
                    {searchTerm && !isLoadingMembers && (
                      <IconButton
                        onClick={() => {
                          setSearchTerm("");
                          setSelectedPayment((prev) => ({
                            ...prev,
                            member_id: "",
                            member_name: "",
                          }));
                        }}
                        size="small"
                      >
                        <Icon>clear</Icon>
                      </IconButton>
                    )}
                    {isLoadingMembers && <CircularProgress color="inherit" size={20} />}
                  </>
                ),
              }}
            />
            {showMemberDropdown && (
              <div
                style={{
                  position: "relative",
                  zIndex: 1300,
                  width: "100%",
                  maxHeight: "200px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  marginTop: "4px",
                }}
              >
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((member) => (
                    <MenuItem
                      key={member.id}
                      onClick={() => handleMemberSelect(member)}
                      sx={{
                        padding: "8px 16px",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      {member.first_name} {member.surname}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ padding: "8px 16px", color: "#999" }}>
                    {isLoadingMembers ? "Loading..." : "No members found"}
                  </MenuItem>
                )}
              </div>
            )}
          </FormControl>

          {!selectedPayment?.member_id && (
            <TextField
              label="Contributor / Group Name"
              placeholder='e.g. "Sunday Offering - Main Service"'
              value={selectedPayment?.contributor_name || ""}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  contributor_name: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              helperText="Used when this offering isn't tied to one specific member"
            />
          )}
        </FormSection>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormSection sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              select
              label="Payment Type"
              value={selectedPayment?.type || "tithe"}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  type: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
            >
              {["offering", "dues", "harvest"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>

            {selectedPayment?.type === "offering" && (
              <TextField
                select
                label="Occasion"
                value={selectedPayment?.occasion || "sunday"}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    occasion: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                required
              >
                {OFFERING_OCCASIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Amount"
              type="number"
              value={selectedPayment?.amount || ""}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  amount: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
            />

            <TextField
              select
              label="Payment Method"
              value={selectedPayment?.method || "manual"}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  method: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
            >
              {["manual", "paystack"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>

          <FormSection sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              label="Reference Number"
              value={selectedPayment?.reference || ""}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  reference: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: !!selectedPayment?.reference,
              }}
            />

            <TextField
              label="Payment Date"
              type="date"
              value={selectedPayment?.paymentDate || new Date().toISOString().split("T")[0]}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  paymentDate: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              required
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              select
              label="Status"
              value={selectedPayment?.status || "active"}
              onChange={(e) =>
                setSelectedPayment({
                  ...selectedPayment,
                  status: e.target.value,
                })
              }
              fullWidth
              margin="normal"
            >
              {["active", "inactive"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>
        </Box>
      </FormDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message="Are you sure you want to delete this payment?"
        confirmLabel="Confirm Delete"
      />
    </Box>
  );
}

Payment.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedPayment: PropTypes.object,
  setSelectedPayment: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddPayment: PropTypes.func,
  members: PropTypes.array,
  isLoadingMembers: PropTypes.bool,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  filteredMembers: PropTypes.array,
  showMemberDropdown: PropTypes.bool,
  setShowMemberDropdown: PropTypes.func,
  handleMemberSelect: PropTypes.func,
  loadAllMembers: PropTypes.func,
};

export default Payment;
